import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, timer, BehaviorSubject, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TaxonomyApiService } from './api-services/taxonomy-api.service';

@Injectable({
  providedIn: 'root',
})
export class PlaygroundService {
  runTestBtnFlag: string = 'disable';
  selectedInputType: 'contactId' | 'audioTranscript' = 'contactId';
  private currentPollingTimeout: any = null;

  playgroundStage: 'input' | 'results' = 'input';
  inputFileFormFlag: boolean = false;
  processedData: any[] = [];
  private processedDataSubject = new BehaviorSubject<any[]>([]);
  processedData$ = this.processedDataSubject.asObservable();

  private playgroundResultStatusSubject = new BehaviorSubject<string>('');
  playgroundResultStatus$ = this.playgroundResultStatusSubject.asObservable();

  playgroundResultStatus: string = '';

  // Add status count tracking
  private statusCountsSubject = new BehaviorSubject<{done: number, total: number}>({done: 0, total: 0});
  statusCounts$ = this.statusCountsSubject.asObservable();

  public taxonomyChange$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private taxonomyApiService: TaxonomyApiService
  ) {
    // Initialize playgroundResultStatus
    this.playgroundResultStatus = this.playgroundResultStatusSubject.value;

    // Listen for taxonomy changes in localStorage
    window.addEventListener('storage', (event) => {
      if (event.key === 'selectedTaxonomy') {
        this.taxonomyChange$.next();
        this.clearPlaygroundData();
      }
    });
  }

  enableRunTestBtnFlag() {
    this.runTestBtnFlag = 'enable';
  }

  disableRunTestBtnFlag() {
    this.runTestBtnFlag = 'disable';
  }

  hideRunTestBtnFlag() {
    this.runTestBtnFlag = 'hide';
  }

  generateInsights(payload: any): Observable<any> {
    return this.taxonomyApiService.generateInsights(payload);
  }

  getInsights(payload: any): Observable<any> {
    return this.taxonomyApiService.getInsights(payload);
  }

  runTest(contactIds: string[][]): Observable<any> {
    // Simulate API delay
    return timer(1000).pipe(
      switchMap(() => {
        this.processedData = [];
        this.setPlaygroundResultStatus('resultPresent');
        return of({ data: [] });
      })
    );
  }

  // Update processedData and notify subscribers
  updateProcessedData(data: any[]) {
    this.processedData = data;
    this.processedDataSubject.next(data);
  }

  setPlaygroundResultStatus(status: string) {
    this.playgroundResultStatus = status;
    this.playgroundResultStatusSubject.next(status);
  }

  updateStatusCounts(statusCounts: {todo: number, in_progress: number, done: number, failed: number}) {
    const total = statusCounts.todo + statusCounts.in_progress + statusCounts.done + statusCounts.failed;
    this.statusCountsSubject.next({
      done: statusCounts.done,
      total: total
    });
  }

  setPollingTimeout(callback: () => void, interval: number) {
    this.currentPollingTimeout = setTimeout(callback, interval);
  }

  cancelCurrentPolling() {
    if (this.currentPollingTimeout) {
      clearTimeout(this.currentPollingTimeout);
      this.currentPollingTimeout = null;
    }
  }

  // Method to clear all playground data
  public clearPlaygroundData() {
    localStorage.removeItem('playgroundData');
    this.processedData = [];
    this.updateStatusCounts({ todo: 0, in_progress: 0, done: 0, failed: 0 });
    this.playgroundStage = 'input';
    this.playgroundResultStatus = 'input';
  }
}
